import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Rating,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import Google from '@mui/icons-material/Google';
import Apple from '@mui/icons-material/Apple';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SEO from '../../components/SEO';
import TutorBookingChangeProposalSection from '../../components/TutorBookingChangeProposalSection';
// import TutorBookingChangeProposalScheduleChip from '../../components/tutor/TutorBookingChangeProposalScheduleChip';
import TutorBookingScheduleBlock from '../../components/tutor/TutorBookingScheduleBlock';
import TutorReviewDialog from '../../components/tutor/TutorReviewDialog';
import TutorSessionDiscoveryMeta from '../../components/tutor/TutorSessionDiscoveryMeta';
import TutorSchoolAffiliation from '../../components/tutor/TutorSchoolAffiliation';
import ChatWithUserIconButton from '../../components/chat/ChatWithUserIconButton';
import RoomBookingUserAvatar from '../../components/RoomBookingUserAvatar';
import { userApiClient } from '../../userApiClient';
import { userTutorCancelReleasesSeries } from '../../utils/userTutorBookingGrouping';
import { hostApiClient } from '../../hostApiClient';
import { tutorApiClient } from '../../tutorApiClient';
import { TutorSkill, TutorSearchResult, UserTutorBookingDetails } from '../../models/Tutor';
import type { TutorReviewWithUsername } from '../../models/TutorReview';
import { TutorBookingChangeStatus } from '../../models/TutorBookingChange';
import { authService } from '../../services/AuthService';
import { getTutorBookingChangeActionErrorMessage } from '../../utils/tutorBookingChangeErrors';
import {
  formatTutorBookingScheduleLabels,
  getProposalNewEndIso,
  getProposalOldEndIso,
  getTutorBookingPreviousTimeLabel,
  proposalTimeChanged,
} from '../../utils/tutorBookingChangeProposal';
import {
  getTutorBookingStatusChipColor,
  getTutorBookingStatusChipVariant,
  isPastTutorBookingStart,
  isUserTutorBookingCancelledStatus,
  normalizeTutorBookingStatus,
  shouldShowTutorLessonAttendanceUi,
} from '../../utils/tutorBookingStatus';
import { tutorBookingStudentNames } from '../../utils/tutorBookingStudentName';
import { isAtTeacherSession } from '../../utils/tutorSessionType';
import { getTutorReviewErrorMessage } from '../../utils/tutorReviewErrors';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import { tutorSkillLabel } from '../../utils/tutorSkillI18n';
import { resolveLanguageLabel } from '../../utils/languageOptions';
import TutorLessonSessionTypeChip from '../../components/tutor/TutorLessonSessionTypeChip';
import TutorBookingPaymentStatusChips from '../../components/tutor/TutorBookingPaymentStatusChips';
import TutorLessonAttendanceChip from '../../components/tutor/TutorLessonAttendanceChip';
import TutorBookingStepperDialog from '../../components/TutorBookingStepperDialog';
import BookingCancellationPolicy from '../../components/BookingCancellationPolicy';
import BookingCreatedAtLabel from '../../components/BookingCreatedAtLabel';
import { parseBookingDisplayInstant } from '../../utils/bookingInstant';
import { shouldShowCancellationPolicyForPayment } from '../../utils/formatCancellationPolicy';
import { getTutorLessonConfirmationErrorMessage } from '../../utils/tutorLessonConfirmation';
import { notifyTutorBookingNeedsActionCountChanged } from '../../utils/tutorBookingNeedsActionCount';
import { resolveTutorById } from '../../utils/resolveTutorById';
import PassBookingChip from '../../components/passes/PassBookingChip';
import BookingStripeFeeBreakdown from '../../components/booking/BookingStripeFeeBreakdown';
import {
  resolveTutorBookingDetailsAudience,
  tutorBookingDetailsListPath,
} from '../../utils/tutorBookingDetailsPath';
import TutorBookingChangeProposalScheduleChip from '../../components/tutor/TutorBookingChangeProposalScheduleChip';
import {
  organizationContactHref,
  organizationContactIcon,
  organizationContactOpensInSameTab,
  visibleOrganizationContacts,
} from '../../utils/organizationContact';

function parseBookingIso(iso: string): Date {
  return parseBookingDisplayInstant(iso);
}

export default function UserTutorBookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const audience = resolveTutorBookingDetailsAudience(location.pathname);
  const isStaff = audience === 'host' || audience === 'tutor';
  const bookingsListPath = tutorBookingDetailsListPath(audience);
  const { t, i18n } = useTranslation();
  const [details, setDetails] = useState<UserTutorBookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [changeActionError, setChangeActionError] = useState<string | null>(null);
  const [processingChange, setProcessingChange] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [hasReviewedTutor, setHasReviewedTutor] = useState(false);
  const [existingReview, setExistingReview] = useState<TutorReviewWithUsername | null>(null);
  const [tutorReviews, setTutorReviews] = useState<TutorReviewWithUsername[]>([]);
  const [allReviewsDialogOpen, setAllReviewsDialogOpen] = useState(false);
  const [confirmingAttendance, setConfirmingAttendance] = useState(false);
  const [confirmAttendanceError, setConfirmAttendanceError] = useState<string | null>(null);
  const [bookAgainOpen, setBookAgainOpen] = useState(false);
  const [bookAgainTutor, setBookAgainTutor] = useState<TutorSearchResult | null>(null);
  const [publicTutor, setPublicTutor] = useState<TutorSearchResult | null>(null);
  const [bookAgainLoading, setBookAgainLoading] = useState(false);
  const [bookAgainError, setBookAgainError] = useState<string | null>(null);
  const [bookAgainLessonOptionId, setBookAgainLessonOptionId] = useState<string | null>(null);
  const [calendarIcsLoading, setCalendarIcsLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const loadDetails = async (bookingId: string) => {
    const data =
      audience === 'host'
        ? await hostApiClient.getHostTutorBookingDetails(bookingId)
        : audience === 'tutor'
          ? await tutorApiClient.getTutorBookingDetails(bookingId)
          : await userApiClient.getTutorBookingDetails(bookingId);
    setDetails(data);
    return data;
  };

  const resolveExistingTutorReview = (
    reviews: TutorReviewWithUsername[],
    reviewId?: string | null,
  ): TutorReviewWithUsername | null => {
    if (reviewId) {
      const byId = reviews.find((review) => review.id === reviewId);
      if (byId) return byId;
    }
    const currentUserId = authService.getUserId();
    if (!currentUserId) return null;
    return reviews.find((review) => review.userId === currentUserId) ?? null;
  };

  const refreshTutorReviews = async (tutorId: string) => {
    try {
      const reviews = await tutorApiClient.getTutorReviews(tutorId);
      setTutorReviews(reviews);
      return reviews;
    } catch (reviewFetchError) {
      console.log('No tutor reviews found or error fetching reviews:', reviewFetchError);
      setTutorReviews([]);
      return [] as TutorReviewWithUsername[];
    }
  };

  const refreshTutorReviewState = async (tutorId: string) => {
    const reviews = await refreshTutorReviews(tutorId);
    try {
      const eligibility = await userApiClient.getTutorReviewEligibility(tutorId);
      if (eligibility.status === 'ALREADY_REVIEWED' || !eligibility.canLeaveReview) {
        setHasReviewedTutor(true);
        setExistingReview(resolveExistingTutorReview(reviews, eligibility.reviewId));
      } else {
        setHasReviewedTutor(false);
        setExistingReview(null);
      }
    } catch (eligibilityError) {
      console.log('Tutor review eligibility unavailable:', eligibilityError);
      const ownReview = resolveExistingTutorReview(reviews, null);
      if (ownReview) {
        setHasReviewedTutor(true);
        setExistingReview(ownReview);
      }
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setError(t('userTutorBookings.detailsError', 'Could not load session details'));
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await loadDetails(id);
        setError(null);
        if (!isStaff && data.tutorId) {
          await refreshTutorReviewState(data.tutorId);
        }
      } catch {
        setError(t('userTutorBookings.detailsError', 'Could not load session details'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, t, audience]);

  useEffect(() => {
    let cancelled = false;
    const tutorId = details?.tutorId;
    if (!tutorId) {
      setPublicTutor(null);
      return undefined;
    }

    (async () => {
      try {
        const tutor = await resolveTutorById(tutorId);
        if (!cancelled) {
          setPublicTutor(tutor);
        }
      } catch {
        if (!cancelled) {
          setPublicTutor(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [details?.tutorId]);

  useEffect(() => {
    if (isStaff) return;
    const onReviewRoute = location.pathname.endsWith('/review');
    if (onReviewRoute && details && !reviewDialogOpen) {
      setReviewDialogOpen(true);
    }
  }, [location.pathname, details, reviewDialogOpen]);

  const handleConfirmAttendance = async () => {
    if (!id) return;
    setConfirmingAttendance(true);
    setConfirmAttendanceError(null);
    try {
      await userApiClient.confirmTutorBookingAttendance(id);
      await loadDetails(id);
      notifyTutorBookingNeedsActionCountChanged();
    } catch (err) {
      setConfirmAttendanceError(getTutorLessonConfirmationErrorMessage(err, t));
      try {
        await loadDetails(id);
      } catch {
        /* keep last known details */
      }
    } finally {
      setConfirmingAttendance(false);
    }
  };

  const handleAcceptChange = async () => {
    if (!id || !details?.changeProposal) return;
    setProcessingChange(true);
    setChangeActionError(null);
    try {
      await userApiClient.acceptTutorBookingChange(id, details.changeProposal.id);
      await loadDetails(id);
      notifyTutorBookingNeedsActionCountChanged();
    } catch (err) {
      setChangeActionError(getTutorBookingChangeActionErrorMessage(err, t));
      try {
        await loadDetails(id);
      } catch {
        /* keep last known details */
      }
    } finally {
      setProcessingChange(false);
    }
  };

  const handleDeclineChange = async (reason: string) => {
    if (!id || !details?.changeProposal) return;
    setProcessingChange(true);
    setChangeActionError(null);
    try {
      await userApiClient.declineTutorBookingChange(id, details.changeProposal.id, { reason });
      await loadDetails(id);
      notifyTutorBookingNeedsActionCountChanged();
    } catch (err) {
      setChangeActionError(getTutorBookingChangeActionErrorMessage(err, t));
      try {
        await loadDetails(id);
      } catch {
        /* keep last known details */
      }
    } finally {
      setProcessingChange(false);
    }
  };

  const bookingEnded = useMemo(() => {
    if (!details?.endTime) return false;
    const end = parseBookingIso(details.endTime);
    return end.getTime() < Date.now();
  }, [details?.endTime]);

  const canCancelBooking =
    !isStaff &&
    !!details &&
    !isUserTutorBookingCancelledStatus(details.status) &&
    !bookingEnded;

  const cancelReleasesSeries = !!details && userTutorCancelReleasesSeries(details);

  const handleCancelBooking = async () => {
    if (!id || !canCancelBooking || !details) return;
    setCancelling(true);
    setCancelError(null);
    try {
      if (cancelReleasesSeries && details.recurringSeriesId) {
        await userApiClient.cancelTutorBookingSeries(details.recurringSeriesId.trim(), cancelReason);
      } else {
        await userApiClient.cancelTutorBooking(id, cancelReason);
      }
      setCancelDialogOpen(false);
      setCancelReason('');
      notifyTutorBookingNeedsActionCountChanged();
      await loadDetails(id);
    } catch {
      setCancelError(t('userTutorBookings.cancelError', 'Failed to cancel booking'));
    } finally {
      setCancelling(false);
    }
  };

  const canWriteReview =
    !isStaff &&
    !!details &&
    normalizeTutorBookingStatus(details.status) === 'CONFIRMED' &&
    bookingEnded &&
    !hasReviewedTutor;

  const handleCreateReview = async () => {
    if (!details || reviewRating === 0) return;

    setSubmittingReview(true);
    setReviewError(null);

    try {
      const created = await userApiClient.createTutorReview({
        tutorId: details.tutorId,
        bookingId: details.id,
        rating: reviewRating,
        comment: reviewComment.trim() || null,
      });

      const comment = reviewComment.trim() || null;
      setHasReviewedTutor(true);
      setExistingReview({
        id: created.id,
        tutorId: details.tutorId,
        userId: authService.getUserId() ?? '',
        bookingId: details.id,
        rating: reviewRating,
        comment,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setReviewDialogOpen(false);
      setReviewRating(0);
      setReviewComment('');

      if (location.pathname.endsWith('/review')) {
        navigate(`/user/tutor-bookings/${id}`, { replace: true });
      }

      await refreshTutorReviews(details.tutorId);
      if (id) {
        await loadDetails(id);
      }
    } catch (err) {
      const message = getTutorReviewErrorMessage(err, t);
      setReviewError(message);
      const raw = String((err as { message?: string })?.message ?? '').toLowerCase();
      if (raw.includes('review already exists') || message === t('review.alreadyReviewedTutor', 'You have already reviewed this tutor.')) {
        setHasReviewedTutor(true);
        await refreshTutorReviewState(details.tutorId);
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleOpenReviewDialog = () => {
    setReviewDialogOpen(true);
    setReviewError(null);
  };

  const handleBookAgain = async (lessonOptionId?: string | null) => {
    if (!details?.tutorId) return;

    setBookAgainLoading(true);
    setBookAgainError(null);
    try {
      const tutor = publicTutor ?? (await resolveTutorById(details.tutorId));
      if (!tutor) {
        setBookAgainError(
          t(
            'userTutorBookings.bookAgainError',
            'Could not open booking for this tutor. Try finding them in tutor search.',
          ),
        );
        return;
      }
      setPublicTutor(tutor);
      setBookAgainTutor(tutor);
      setBookAgainLessonOptionId(
        lessonOptionId !== undefined ? lessonOptionId : details.lessonOptionId ?? null,
      );
      setBookAgainOpen(true);
    } catch {
      setBookAgainError(
        t(
          'userTutorBookings.bookAgainError',
          'Could not open booking for this tutor. Try finding them in tutor search.',
        ),
      );
    } finally {
      setBookAgainLoading(false);
    }
  };

  const handleCloseBookAgain = () => {
    setBookAgainOpen(false);
    setBookAgainLessonOptionId(null);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewRating(0);
    setReviewComment('');
    setReviewError(null);

    if (location.pathname.endsWith('/review')) {
      navigate(`/user/tutor-bookings/${id}`, { replace: true });
    }
  };

  const formatMoney = (amount: number, currencyCode: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode || 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    }
    return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
  };

  const startTime = details?.startTime ?? '';
  const endTime = details?.endTime ?? '';
  const proposal = details?.changeProposal;
  const pendingProposal = proposal?.status === TutorBookingChangeStatus.PENDING_REPLY;
  // const previousVisibleInline =
  //   !!pendingProposal && !!proposal && proposalTimeChanged(proposal);

  const scheduleDisplay = useMemo(() => {
    if (!startTime || !endTime) {
      return {
        schedule: { dateLabel: '', timeLabel: '', durationLabel: '' },
        previousTimeLabel: undefined as string | undefined,
        emphasize: false,
      };
    }

    const proposalScheduleOptions = {
      source: 'proposal' as const,
      timezone: details?.timezone,
    };

    if (pendingProposal && proposal) {
      const proposedEndIso = getProposalNewEndIso(proposal, startTime, endTime);
      const proposedSchedule = formatTutorBookingScheduleLabels(
        proposal.newStartTime,
        proposedEndIso,
        i18n.language,
        formatDuration,
        proposalScheduleOptions,
      );
      if (proposalTimeChanged(proposal)) {
        const oldEndIso = getProposalOldEndIso(proposal, startTime, endTime);
        const previousSchedule = formatTutorBookingScheduleLabels(
          proposal.oldStartTime,
          oldEndIso,
          i18n.language,
          formatDuration,
          proposalScheduleOptions,
        );
        return {
          schedule: proposedSchedule,
          previousTimeLabel: getTutorBookingPreviousTimeLabel(previousSchedule, proposedSchedule),
          emphasize: true,
        };
      }
      return { schedule: proposedSchedule, previousTimeLabel: undefined, emphasize: true };
    }

    const effectiveStart =
      proposal?.status === TutorBookingChangeStatus.ACCEPTED ? proposal.newStartTime : startTime;
    const effectiveEnd =
      proposal?.status === TutorBookingChangeStatus.ACCEPTED
        ? getProposalNewEndIso(proposal, startTime, endTime)
        : endTime;

    return {
      schedule: formatTutorBookingScheduleLabels(
        effectiveStart,
        effectiveEnd,
        i18n.language,
        formatDuration,
        proposal?.status === TutorBookingChangeStatus.ACCEPTED
          ? proposalScheduleOptions
          : undefined,
      ),
      previousTimeLabel: undefined,
      emphasize: false,
    };
  }, [details?.timezone, endTime, i18n.language, pendingProposal, proposal, startTime, t]);

  const changeProposalStatusChip =
    proposal && startTime && endTime ? (
      <TutorBookingChangeProposalScheduleChip
        proposal={proposal}
        bookingStartTime={startTime}
        bookingEndTime={endTime}
        locale={i18n.language}
        audience={audience === 'user' ? 'user' : 'host'}
        timezone={details?.timezone}
        showPreviousTimeInTooltip={!(pendingProposal && proposalTimeChanged(proposal))}
      />
    ) : null;

  const studentNames = tutorBookingStudentNames({
    studentProfile: details?.studentProfile,
    userName: details?.userName,
  });
  const studentDisplayName =
    studentNames.primaryName ||
    t(audience === 'tutor' ? 'tutorBookings.user' : 'hostTutorBookings.user', 'Student');

  const statusLabel = (status: string) => {
    const key = `userTutorBookings.bookingStatus.${normalizeTutorBookingStatus(status)}`;
    return t(key, status);
  };

  const renderReviewMeta = () => {
    const reviewCount =
      tutorReviews.length > 0
        ? tutorReviews.length
        : details?.reviewCount ?? publicTutor?.reviewCount ?? 0;
    const avgReviewRating =
      tutorReviews.length > 0
        ? tutorReviews.reduce((sum, review) => sum + review.rating, 0) / tutorReviews.length
        : details?.avgReviewRating ?? publicTutor?.avgReviewRating ?? 0;
    const reviewPending =
      !isStaff &&
      !!details &&
      normalizeTutorBookingStatus(details.status) === 'CONFIRMED' &&
      !bookingEnded &&
      !hasReviewedTutor;
    const reviewAvailableAfterLabel = t(
      'bookingDetails.reviewAvailableAfterFirstBooking',
      'Review will be available after your first booking ends',
    );
    const tutorRatingSx = {
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle',
      '& .MuiRating-icon': { fontSize: 18 },
      '& .MuiRating-iconFilled': { color: '#F8C14A' },
      '& .MuiRating-iconEmpty': { color: 'action.disabled' },
    } as const;
    const reviewMetaTextProps = {
      variant: 'body2' as const,
      color: 'text.secondary' as const,
      sx: { fontSize: '0.875rem', lineHeight: 1.4 },
    };
    const reviewStarsColumnSx = {
      width: 100,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    } as const;

    const renderReadOnlyTutorRating = (name: string, value = reviewCount > 0 ? avgReviewRating : 0) => (
      <Rating
        name={name}
        value={value}
        precision={0.1}
        readOnly
        size="small"
        icon={<StarRoundedIcon fontSize="inherit" />}
        emptyIcon={<StarRoundedIcon fontSize="inherit" />}
        sx={tutorRatingSx}
      />
    );

    const renderRatingSummary = (name: string) => {
      const reviewCountLabel = `(${t('review.reviewCount', { count: reviewCount })})`;

      return (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={reviewStarsColumnSx}>{renderReadOnlyTutorRating(name)}</Box>
          <Typography
            {...reviewMetaTextProps}
            component="span"
            sx={{
              ...reviewMetaTextProps.sx,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              lineHeight: '18px',
            }}
          >
            <span>{avgReviewRating.toFixed(1)}</span>
            {reviewCount > 0 ? (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (details?.tutorId) {
                    void refreshTutorReviews(details.tutorId);
                  }
                  setAllReviewsDialogOpen(true);
                }}
                sx={{
                  border: 0,
                  bgcolor: 'transparent',
                  p: 0,
                  m: 0,
                  cursor: 'pointer',
                  color: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  fontFamily: 'inherit',
                  fontWeight: 'inherit',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {reviewCountLabel}
              </Box>
            ) : (
              <span>{reviewCountLabel}</span>
            )}
          </Typography>
        </Stack>
      );
    };

    const renderYourRating = () => {
      if (!existingReview) return null;

      const comment = existingReview.comment?.trim() || null;
      const row = (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          component="span"
          sx={{
            display: 'inline-flex',
            cursor: comment ? 'help' : 'default',
          }}
        >
          <Box sx={reviewStarsColumnSx}>
            {renderReadOnlyTutorRating('tutor-booking-your-rating', existingReview.rating)}
          </Box>
          <Typography {...reviewMetaTextProps} component="span" fontWeight={600}>
            {t('bookingDetails.ratedByYou', 'Rated by You')}
          </Typography>
        </Stack>
      );

      if (!comment) return row;

      return (
        <Tooltip
          title={comment}
          placement="top"
          arrow
          enterTouchDelay={0}
          slotProps={{
            tooltip: {
              sx: { maxWidth: 280, whiteSpace: 'pre-wrap' },
            },
          }}
        >
          {row}
        </Tooltip>
      );
    };

    if (canWriteReview) {
      return (
        <Stack spacing={0.75} alignItems="flex-start">
          {reviewCount > 0 ? renderRatingSummary('tutor-booking-tutor-rating') : null}
          <Rating
            name="tutor-booking-review"
            value={0}
            onChange={(_, newValue) => {
              setReviewRating(newValue ?? 0);
              handleOpenReviewDialog();
            }}
            precision={1}
            size="medium"
            sx={{
              alignSelf: 'flex-start',
              '& .MuiRating-iconFilled': { color: '#F8C14A' },
              '& .MuiRating-iconHover': { color: '#F8C14A' },
            }}
          />
        </Stack>
      );
    }

    if (reviewPending) {
      return (
        <Box
          component="span"
          tabIndex={0}
          aria-label={reviewAvailableAfterLabel}
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'flex-start',
            gap: 0.5,
            cursor: 'help',
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
            mx: -0.5,
            outline: 'none',
            '&:hover, &:focus-visible': { bgcolor: 'action.hover' },
            '& .review-available-hint': {
              opacity: 0,
              maxHeight: 0,
              overflow: 'hidden',
              transition: 'opacity 120ms ease, max-height 120ms ease',
            },
            '&:hover .review-available-hint, &:focus-visible .review-available-hint': {
              opacity: 1,
              maxHeight: 48,
            },
          }}
        >
          {renderRatingSummary('tutor-booking-review-pending')}
          <Typography className="review-available-hint" variant="caption" color="text.secondary">
            {reviewAvailableAfterLabel}
          </Typography>
        </Box>
      );
    }

    if (hasReviewedTutor && existingReview) {
      return (
        <Stack spacing={0.75} alignItems="flex-start">
          {renderRatingSummary('tutor-booking-tutor-rating-reviewed')}
          {renderYourRating()}
        </Stack>
      );
    }

    return renderRatingSummary('tutor-booking-tutor-rating');
  };

  const tutorDisplayName = details?.tutorName || publicTutor?.name || '';
  const tutorHeadline = (details?.headline ?? publicTutor?.headline)?.trim() || '';
  const tutorOverview = (details?.overview ?? publicTutor?.overview)?.trim() || '';
  const tutorSkills = details?.skills?.length ? details.skills : publicTutor?.skills ?? [];
  const tutorLanguages = details?.languages?.length ? details.languages : publicTutor?.languages ?? [];
  const tutorBookingCount = details?.bookingCount ?? publicTutor?.bookingCount ?? 0;
  const tutorStudentCount = details?.studentCount ?? publicTutor?.studentCount ?? 0;
  const tutorProfileImage =
    (details?.tutorProfileImageUrl ?? details?.profileImageUrl ?? publicTutor?.profileImageUrl)?.trim() ||
    undefined;
  const visibleContacts = visibleOrganizationContacts(details?.contacts);
  const hasOrganizationContact = visibleContacts.length > 0;

  const formatLessonLocationAddress = (booking: UserTutorBookingDetails) => {
    const parts: string[] = [];
    if (booking.spaceAddressLine1?.trim()) parts.push(booking.spaceAddressLine1.trim());
    if (booking.spaceAddressLine2?.trim()) parts.push(booking.spaceAddressLine2.trim());
    if (booking.spaceCity?.trim()) parts.push(booking.spaceCity.trim());
    if (booking.spaceState?.trim()) parts.push(booking.spaceState.trim());
    if (booking.spaceZipCode?.trim()) parts.push(booking.spaceZipCode.trim());
    return parts.join(', ');
  };

  const lessonMapsUrl = details?.spaceGoogleMapsUrl?.trim() || null;
  const lessonLocationAddress = details ? formatLessonLocationAddress(details) : '';
  const showOfflineLocation =
    isAtTeacherSession(details?.lessonSessionType) &&
    Boolean(lessonMapsUrl || lessonLocationAddress);
  const showCalendarLinks =
    !isStaff &&
    !!details &&
    !isUserTutorBookingCancelledStatus(details.status) &&
    Boolean(details.calendarLinks?.google || details.calendarLinks?.ics);

  const openGoogleCalendar = () => {
    const url = details?.calendarLinks?.google;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openIcsCalendar = async () => {
    const ics = details?.calendarLinks?.ics;
    if (!ics || !details) return;
    setCalendarIcsLoading(true);
    try {
      const blob = await userApiClient.fetchCalendarIcs(ics);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `tutor-booking-${details.id}.ics`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (calendarErr) {
      console.error(t('bookingDetails.calendarIcsError', 'Could not download calendar file.'), calendarErr);
    } finally {
      setCalendarIcsLoading(false);
    }
  };

  return (
    <>
      <SEO title={t('userTutorBookings.sessionDetails')} noindex />
      <Box sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 }, pb: { xs: 8, sm: 10 } }}>
        <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" sx={{ minWidth: 0 }}>
              {t('userTutorBookings.sessionDetails')}
            </Typography>
            <Chip
              label={`← ${
                isStaff
                  ? t('roomBookingRequests.backToRequests', 'Back to requests')
                  : t('bookingDetails.backToBookings', 'Back to My Bookings')
              }`}
              onClick={() => navigate(bookingsListPath)}
              sx={{
                flexShrink: 0,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                bgcolor: 'transparent',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'transparent' },
              }}
            />
          </Stack>
          <Divider sx={{ mb: 3 }} />

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && details && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                {isStaff ? (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {t('roomBookingRequests.booker', 'Booker')}
                    </Typography>
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <RoomBookingUserAvatar
                          userProfileImageUrl={details.userProfileImageUrl}
                          displayName={studentDisplayName}
                        />
                        <Box minWidth={0}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body1" fontWeight={700} noWrap>
                              {studentDisplayName}
                            </Typography>
                            {details.userId?.trim() ? (
                              <ChatWithUserIconButton
                                partnerUserId={details.userId}
                                partnerName={studentDisplayName}
                                basePath={audience === 'host' ? '/host/chats' : '/tutor/chats'}
                                sx={{ flexShrink: 0 }}
                              />
                            ) : null}
                          </Stack>
                          {studentNames.accountAlias ? (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {studentNames.accountAlias}
                            </Typography>
                          ) : null}
                          {details.userPhoneNumber ? (
                            <Stack direction="row" spacing={0.35} alignItems="center">
                              <PhoneOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                component="a"
                                href={`tel:${details.userPhoneNumber}`}
                                sx={{ textDecoration: 'none' }}
                              >
                                {details.userPhoneNumber}
                              </Typography>
                            </Stack>
                          ) : null}
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>
                ) : null}
                <Card
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    background:
                      'linear-gradient(160deg, rgba(78,103,235,0.08) 0%, rgba(78,103,235,0.02) 55%, transparent 100%)',
                  }}
                >
                  <Stack spacing={2} sx={{ flex: 1 }}>
                    <Stack spacing={0.75}>
                      <Chip
                        color={getTutorBookingStatusChipColor(details.status)}
                        variant={getTutorBookingStatusChipVariant(details.status)}
                        label={
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto 1fr',
                              alignItems: 'center',
                              width: '100%',
                              columnGap: 0.75,
                            }}
                          >
                            <Box aria-hidden />
                            <Box component="span" sx={{ textAlign: 'center', fontWeight: 700 }}>
                              {statusLabel(details.status)}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                              {shouldShowTutorLessonAttendanceUi({
                                status: details.status,
                                userConfirmedAt: details.userConfirmedAt,
                                confirmAttendanceEnabled: details.confirmAttendanceEnabled,
                              }) ? (
                                <TutorLessonAttendanceChip
                                  status={details.status}
                                  userConfirmedAt={details.userConfirmedAt}
                                  confirmAttendanceEnabled={details.confirmAttendanceEnabled}
                                  size="medium"
                                  confirming={!isStaff && confirmingAttendance}
                                  onConfirm={isStaff ? undefined : handleConfirmAttendance}
                                  tone="inherit"
                                />
                              ) : null}
                            </Box>
                          </Box>
                        }
                        sx={{
                          width: '100%',
                          height: 48,
                          borderRadius: 1.5,
                          fontWeight: 700,
                          fontSize: '0.9375rem',
                          letterSpacing: '0.02em',
                          '& .MuiChip-label': {
                            px: 1.5,
                            width: '100%',
                            display: 'block',
                            whiteSpace: 'normal',
                            lineHeight: 1.25,
                          },
                        }}
                      />
                      {confirmAttendanceError && !isStaff ? (
                        <Alert severity="error" sx={{ py: 0.5 }} onClose={() => setConfirmAttendanceError(null)}>
                          {confirmAttendanceError}
                        </Alert>
                      ) : null}
                    </Stack>

                    {details.changeProposal && !isStaff ? (
                      <TutorBookingChangeProposalSection
                        proposal={details.changeProposal}
                        bookingStartTime={details.startTime}
                        bookingEndTime={details.endTime}
                        timezone={details.timezone}
                        processing={processingChange}
                        actionError={changeActionError}
                        embedded
                        hideSchedule
                        scheduleBlockProps={{
                          schedule: scheduleDisplay.schedule,
                          emphasize: scheduleDisplay.emphasize,
                          previousTimeLabel: scheduleDisplay.previousTimeLabel,
                        }}
                        onAccept={handleAcceptChange}
                        onDecline={handleDeclineChange}
                      />
                    ) : (
                      <TutorBookingScheduleBlock
                        schedule={scheduleDisplay.schedule}
                        emphasize={scheduleDisplay.emphasize}
                        previousTimeLabel={scheduleDisplay.previousTimeLabel}
                        statusChip={isStaff ? changeProposalStatusChip : undefined}
                      />
                    )}

                    {showOfflineLocation ? (
                      <Box
                        onClick={() => {
                          if (lessonMapsUrl) window.open(lessonMapsUrl, '_blank');
                        }}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          cursor: lessonMapsUrl ? 'pointer' : 'default',
                          transition: 'border-color 0.2s ease, background-color 0.2s ease',
                          ...(lessonMapsUrl
                            ? {
                                '&:hover': {
                                  bgcolor: 'background.paper',
                                  borderColor: 'primary.main',
                                },
                              }
                            : {}),
                        }}
                      >
                        <LocationOnIcon
                          sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0, alignSelf: 'center' }}
                        />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                          {lessonLocationAddress ||
                            details.spaceName?.trim() ||
                            t('userTutorBookings.locationUnavailable', 'Location unavailable')}
                        </Typography>
                        {lessonMapsUrl ? (
                          <Tooltip title={t('chat.files.openInNewTab', 'Open in new tab')}>
                            <IconButton
                              size="small"
                              aria-label={t('chat.files.openInNewTab', 'Open in new tab')}
                              onClick={(event) => {
                                event.stopPropagation();
                                window.open(lessonMapsUrl, '_blank');
                              }}
                              sx={{
                                flexShrink: 0,
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    ) : null}

                    {showCalendarLinks ? (
                      <Box
                        sx={{
                          py: 1.5,
                          px: 1.5,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <CalendarMonthOutlinedIcon
                              sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                            />
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
                              {t('bookingDetails.addToCalendar', 'Add to Calendar')}
                            </Typography>
                          </Stack>
                          <Box sx={{ pl: 3, width: '100%', boxSizing: 'border-box' }}>
                            <ToggleButtonGroup
                              exclusive
                              value={null}
                              onChange={(_event, value: string | null) => {
                                if (value === 'google') {
                                  openGoogleCalendar();
                                } else if (value === 'apple' || value === 'ics') {
                                  void openIcsCalendar();
                                }
                              }}
                              sx={{
                                width: '100%',
                                display: 'flex',
                                '& .MuiToggleButton-root': {
                                  flex: 1,
                                  justifyContent: 'center',
                                  px: 1.5,
                                  py: 0.75,
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  lineHeight: 1.25,
                                  textTransform: 'none',
                                  gap: 0.75,
                                  borderColor: 'divider',
                                  color: 'text.primary',
                                  bgcolor: 'background.paper',
                                  '&.Mui-selected': {
                                    bgcolor: 'background.paper',
                                    color: 'text.primary',
                                  },
                                  '&.Mui-selected:hover': {
                                    bgcolor: 'action.hover',
                                  },
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                  },
                                },
                              }}
                            >
                              {details.calendarLinks?.google ? (
                                <ToggleButton
                                  value="google"
                                  title={t('bookingDetails.calendarGoogle', 'Google Calendar')}
                                >
                                  <Google sx={{ fontSize: 16 }} />
                                  Google
                                </ToggleButton>
                              ) : null}
                              {details.calendarLinks?.ics ? (
                                <ToggleButton
                                  value="apple"
                                  disabled={calendarIcsLoading}
                                  title={t('bookingDetails.calendarApple', 'Apple Calendar')}
                                >
                                  {calendarIcsLoading ? (
                                    <CircularProgress color="inherit" size={14} />
                                  ) : (
                                    <Apple sx={{ fontSize: 16 }} />
                                  )}
                                  Apple
                                </ToggleButton>
                              ) : null}
                              {details.calendarLinks?.ics ? (
                                <ToggleButton
                                  value="ics"
                                  disabled={calendarIcsLoading}
                                  title={t('bookingDetails.calendarIcs', 'ICS file')}
                                >
                                  {calendarIcsLoading ? (
                                    <CircularProgress color="inherit" size={14} />
                                  ) : (
                                    <DescriptionIcon sx={{ fontSize: 16 }} />
                                  )}
                                  ICS
                                </ToggleButton>
                              ) : null}
                            </ToggleButtonGroup>
                          </Box>
                        </Stack>
                      </Box>
                    ) : null}

                    {details.roomName?.trim() ? (
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <MeetingRoomRoundedIcon
                            sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                          />
                          <Stack spacing={0.25} minWidth={0} sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                              {details.roomName.trim()}
                            </Typography>
                            {details.spaceName?.trim() ? (
                              <Typography variant="caption" color="text.secondary">
                                {details.spaceName.trim()}
                              </Typography>
                            ) : null}
                          </Stack>
                        </Stack>
                      </Box>
                    ) : null}

                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Stack spacing={1}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <MusicNoteRoundedIcon
                          sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Stack spacing={0.75} minWidth={0} sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {details.lessonOptionName?.trim() ||
                              t('userTutorBookings.tutorSession')}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <TutorLessonSessionTypeChip sessionType={details.lessonSessionType} />
                            <TutorBookingPaymentStatusChips
                              paymentStatus={details.pass ? undefined : details.paymentStatus}
                              stripeRefundStatus={details.stripeRefundStatus ?? undefined}
                              compact
                            />
                            {details.pass && <PassBookingChip pass={details.pass} />}
                          </Stack>
                        </Stack>
                        <Stack
                          spacing={0.15}
                          alignItems="flex-end"
                          justifyContent="center"
                          sx={{ flexShrink: 0, alignSelf: 'center' }}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PaymentsRoundedIcon
                              sx={{
                                fontSize: 18,
                                color: details.pass ? 'text.secondary' : 'text.primary',
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={details.pass ? 'text.secondary' : 'text.primary'}
                              sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                lineHeight: 1.2,
                                textDecoration: details.pass ? 'line-through' : 'none',
                              }}
                            >
                              {formatMoney(details.totalAmount, details.currencyCode)}
                            </Typography>
                          </Stack>
                          <BookingStripeFeeBreakdown
                            showStaffFees={isStaff}
                            bookingTotalAmount={details.totalAmount}
                            bookingCommissionPercent={details.bookingCommissionPercent}
                            bookingCommissionAmount={details.bookingCommissionAmount}
                            stripeProcessingFeeAmount={details.stripeProcessingFeeAmount}
                            stripeProcessingFeeCurrency={details.stripeProcessingFeeCurrency}
                            stripeReceiptUrl={details.stripeReceiptUrl}
                            bookingCurrencyCode={details.currencyCode}
                          />
                        </Stack>
                      </Stack>
                      </Stack>
                    </Box>

                    {!isUserTutorBookingCancelledStatus(details.status) &&
                    !isPastTutorBookingStart(details.startTime) &&
                    shouldShowCancellationPolicyForPayment(details.paymentStatus) ? (
                      <BookingCancellationPolicy
                        startTime={details.startTime}
                        timezone={details.timezone}
                        policy={details.cancellationPolicy}
                        showWhenMissing={false}
                        density="comfortable"
                      />
                    ) : null}
                    {canCancelBooking ? (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setCancelError(null);
                          setCancelReason('');
                          setCancelDialogOpen(true);
                        }}
                        fullWidth
                      >
                        {cancelReleasesSeries
                          ? t('userTutorBookings.cancelAll', 'Cancel all')
                          : t('bookings.cancelBooking', 'Cancel Booking')}
                      </Button>
                    ) : null}
                    <BookingCreatedAtLabel
                      createdAt={details.createdAt}
                      timezone={details.timezone}
                      placement="footer"
                    />
                  </Stack>
                </Card>
                </Stack>
              </Grid>

              <Grid item xs={12} md={8}>
                <Stack spacing={2.5}>
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={0}
                      alignItems="stretch"
                      sx={{ width: '100%' }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 2, sm: 3 }}
                        alignItems={{ xs: 'center', sm: 'flex-start' }}
                        flex={hasOrganizationContact ? 5 : 1}
                        minWidth={0}
                      >
                      <Avatar
                        src={tutorProfileImage}
                        alt=""
                        variant="rounded"
                        sx={{
                          width: { xs: '100%', sm: 160, md: 180 },
                          maxWidth: { xs: 240, sm: 160, md: 180 },
                          height: { xs: 180, sm: 160, md: 180 },
                          borderRadius: 2,
                          bgcolor: '#4AA7C6',
                          color: '#ffffff',
                          fontSize: { xs: '2.5rem', md: '3rem' },
                        }}
                      >
                        {tutorAvatarInitials(tutorDisplayName)}
                      </Avatar>

                      <Stack spacing={1.25} flex={1} minWidth={0} sx={{ width: '100%' }}>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography
                            variant="h4"
                            noWrap
                            sx={{
                              lineHeight: 1.15,
                              fontSize: { xs: '1.5rem', sm: '2rem' },
                              minWidth: 0,
                            }}
                          >
                            {tutorDisplayName}
                          </Typography>
                          {details.tutorUserId?.trim() && audience !== 'tutor' ? (
                            <ChatWithUserIconButton
                              partnerUserId={details.tutorUserId}
                              partnerName={tutorDisplayName}
                              basePath={audience === 'host' ? '/host/chats' : '/user/chats'}
                              sx={{ flexShrink: 0 }}
                            />
                          ) : null}
                        </Stack>
                        <TutorSchoolAffiliation
                          organizationType={
                            details.organizationType ?? publicTutor?.organizationType
                          }
                          spaceId={details.spaceId ?? publicTutor?.spaceId}
                          spaceName={details.spaceName ?? publicTutor?.spaceName}
                        />
                        {tutorHeadline ? (
                          <Typography variant="subtitle1" color="text.secondary">
                            {tutorHeadline}
                          </Typography>
                        ) : null}

                        {bookAgainError ? (
                          <Alert severity="error" sx={{ py: 0.5 }}>
                            {bookAgainError}
                          </Alert>
                        ) : null}

                        {renderReviewMeta()}

                        <TutorSessionDiscoveryMeta
                          supportedSessionTypes={
                            publicTutor?.supportedSessionTypes ??
                            (details.lessonSessionType ? [details.lessonSessionType] : undefined)
                          }
                        />

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.25 }}>
                          <Chip
                            icon={<EventAvailableRoundedIcon />}
                            label={t('tutorSearch.statsConfirmedBookings', 'Bookings: {{count}}', {
                              count: tutorBookingCount,
                            })}
                            variant="outlined"
                          />
                          <Chip
                            icon={<GroupsRoundedIcon />}
                            label={t('tutorSearch.statsStudents', 'Students: {{count}}', {
                              count: tutorStudentCount,
                            })}
                            variant="outlined"
                          />
                        </Stack>

                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ReplayRoundedIcon />}
                          onClick={() => handleBookAgain(details.lessonOptionId ?? null)}
                          disabled={bookAgainLoading}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {bookAgainLoading
                            ? t('common.loading', 'Loading...')
                            : t('bookingDetails.bookAgain', 'Book again')}
                        </Button>
                      </Stack>
                      </Stack>

                      {hasOrganizationContact ? (
                        <>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                              display: { xs: 'none', md: 'block' },
                              mx: 2,
                              borderColor: 'divider',
                              opacity: 0.35,
                            }}
                          />
                          <Divider
                            sx={{
                              display: { xs: 'block', md: 'none' },
                              my: 2,
                              borderColor: 'divider',
                              opacity: 0.35,
                            }}
                          />
                          <Box sx={{ flex: 2, minWidth: { md: 148 } }}>
                            <Typography
                              variant="overline"
                              color="text.secondary"
                              sx={{ display: 'block', mb: 1, lineHeight: 1.15 }}
                            >
                              {t('space.contact', 'Contact')}
                            </Typography>
                            <Stack spacing={1.25}>
                              {visibleContacts.map((contact, index) => {
                                const ContactIcon = organizationContactIcon(contact);
                                const href = organizationContactHref(contact);
                                const opensInSameTab = organizationContactOpensInSameTab(contact);
                                return (
                                  <Box
                                    key={`contact-${contact.type}-${index}`}
                                    component="a"
                                    href={href}
                                    target={opensInSameTab ? '_self' : '_blank'}
                                    rel={opensInSameTab ? undefined : 'noopener noreferrer'}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.25,
                                      p: 1.5,
                                      borderRadius: 1.5,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                      '&:hover': {
                                        bgcolor: 'action.hover',
                                        borderColor: 'primary.main',
                                      },
                                    }}
                                  >
                                    <ContactIcon
                                      sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }}
                                    />
                                    <Box minWidth={0}>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{ wordBreak: 'break-word' }}
                                      >
                                        {contact.value}
                                      </Typography>
                                      {contact.comment?.trim() ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ wordBreak: 'break-word' }}
                                        >
                                          {contact.comment.trim()}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        </>
                      ) : null}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" spacing={0.55} alignItems="center">
                        <MusicNoteRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="overline" color="text.secondary">
                          {t('tutorSearch.skills', 'Skills')}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                        {tutorSkills.length > 0 ? (
                          tutorSkills.map((skill) => (
                            <Chip
                              key={`skill-${skill}`}
                              size="small"
                              label={tutorSkillLabel(t, skill as TutorSkill)}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" spacing={0.55} alignItems="center">
                        <TranslateRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="overline" color="text.secondary">
                          {t('tutorSearch.languages', 'Languages')}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                        {tutorLanguages.length > 0 ? (
                          tutorLanguages.map((code) => (
                            <Chip
                              key={`lang-${code}`}
                              size="small"
                              label={resolveLanguageLabel(code, i18n.language)}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Box>

                  {tutorOverview ? (
                    <Box
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {t('tutor.profileCompletion.overview', 'Overview')}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {tutorOverview}
                      </Typography>
                    </Box>
                  ) : null}

                  {isStaff && details.reason?.trim() ? (
                    <Box
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography variant="overline" color="text.secondary">
                        {t('hostTutorBookings.reason', 'Reason')}
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                        {details.reason.trim()}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Grid>
            </Grid>
          )}
        </Card>
      </Box>

      {details && !isStaff && (
        <TutorReviewDialog
          open={reviewDialogOpen}
          tutorName={details.tutorName}
          canSubmit={canWriteReview}
          rating={reviewRating}
          comment={reviewComment}
          submitting={submittingReview}
          error={reviewError}
          onClose={handleCloseReviewDialog}
          onRatingChange={setReviewRating}
          onCommentChange={setReviewComment}
          onSubmit={handleCreateReview}
        />
      )}

      {!isStaff && (
        <Dialog open={cancelDialogOpen} onClose={() => !cancelling && setCancelDialogOpen(false)}>
          <DialogTitle>
            {cancelReleasesSeries
              ? t('userTutorBookings.cancelAllTitle', 'Cancel all lessons?')
              : t('bookings.cancelBooking')}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {cancelReleasesSeries
                ? t(
                    'userTutorBookings.cancelAllBody',
                    'These lessons are still waiting for approval. Cancelling releases the card hold for the whole series.',
                  )
                : t('userTutorBookings.cancelConfirmation')}
            </Typography>
            {details &&
            !isPastTutorBookingStart(details.startTime) &&
            shouldShowCancellationPolicyForPayment(details.paymentStatus) ? (
              <BookingCancellationPolicy
                startTime={details.startTime}
                timezone={details.timezone}
                policy={details.cancellationPolicy}
                showWhenMissing={false}
                density="comfortable"
                sx={{ mb: 2 }}
              />
            ) : null}
            {cancelError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {cancelError}
              </Alert>
            ) : null}
            <TextField
              fullWidth
              margin="dense"
              label={t('userTutorBookings.cancelReasonOptional')}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
              {t('bookings.keepBooking')}
            </Button>
            <Button
              onClick={handleCancelBooking}
              color="error"
              variant="contained"
              disabled={cancelling}
            >
              {cancelling
                ? t('bookings.cancelling')
                : cancelReleasesSeries
                  ? t('userTutorBookings.cancelAll', 'Cancel all')
                  : t('bookings.confirmCancel')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={allReviewsDialogOpen}
        onClose={() => setAllReviewsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
          <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {t('review.allReviews', 'All Reviews')} (
                {tutorReviews.length > 0
                  ? tutorReviews.length
                  : details?.reviewCount ?? publicTutor?.reviewCount ?? 0}
                )
              </Typography>
              <IconButton
                aria-label={t('common.close', 'Close')}
                onClick={() => setAllReviewsDialogOpen(false)}
                sx={{ color: (theme) => theme.palette.grey[500] }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 2, pt: 0 }}>
            {(() => {
              const reviewCount =
                tutorReviews.length > 0
                  ? tutorReviews.length
                  : details?.reviewCount ?? publicTutor?.reviewCount ?? 0;
              const avgReviewRating =
                tutorReviews.length > 0
                  ? tutorReviews.reduce((sum, review) => sum + review.rating, 0) / tutorReviews.length
                  : details?.avgReviewRating ?? publicTutor?.avgReviewRating ?? 0;

              return reviewCount > 0 ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Rating
                    value={avgReviewRating}
                    readOnly
                    precision={0.1}
                    size="small"
                    icon={<StarRoundedIcon fontSize="inherit" />}
                    emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                    sx={{
                      '& .MuiRating-iconFilled': { color: '#F8C14A' },
                      '& .MuiRating-iconEmpty': { color: 'action.disabled' },
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {avgReviewRating.toFixed(1)} · {t('review.reviewCount', { count: reviewCount })}
                  </Typography>
                </Stack>
              ) : null;
            })()}
            <Stack spacing={2} divider={<Divider flexItem />}>
              {[...tutorReviews]
                .sort(
                  (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                )
                .map((review) => (
                  <Box key={review.id}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <RoomBookingUserAvatar
                        displayName={review.username?.trim() || '?'}
                        size={36}
                      />
                      <Box minWidth={0} sx={{ flex: 1 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {review.username?.trim() ||
                              t('review.anonymousReviewer', 'Anonymous student')}
                          </Typography>
                          <Rating value={review.rating} readOnly size="small" precision={0.1} />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(review.createdAt).toLocaleDateString(i18n.language, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                        </Stack>
                        {review.comment?.trim() ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {review.comment.trim()}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                  </Box>
                ))}
            </Stack>
          </DialogContent>
        </Dialog>

      <TutorBookingStepperDialog
        open={bookAgainOpen}
        tutor={bookAgainTutor}
        initialLessonOptionId={bookAgainLessonOptionId}
        onClose={handleCloseBookAgain}
      />
    </>
  );
}
